import { IconContext } from "react-icons";
import { createRoot } from "react-dom/client";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

import "./favicon.ico";
import "./styles/tailwind.css";
import "./styles/index.scss";
import "./i18n";

import ManagePage from "./components/ManagePage";

import ToastViewport from "./components/ToastViewport";
import LoadingOverlay from "./components/LoadingOverlay";
import { initAuth, isAuthConfigured, loadAccounts, loadConsumers, getActiveConsumer, getUser } from "./libs/auth";
import { toast } from "./libs/toast";
import { applyPageMeta } from "./libs/page-meta";
import i18n from "./i18n";

// Each page loads on demand — the home page doesn't ship the editor.
const App = lazy(() => import("./components/App"));
const DetailPage = lazy(() => import("./components/DetailPage"));
const ComparePage = lazy(() => import("./components/ComparePage"));
const AdminPage = lazy(() => import("./components/AdminPage"));
const EmbedPage = lazy(() => import("./components/EmbedPage"));

/** While a page's code loads: the boot loader from index.html. */
function PageLoading() {
  useEffect(() => {
    const loader = document.querySelector(".loading");
    if (!loader) return;
    loader.style.display = "flex";
    return () => { loader.style.display = "none"; };
  }, []);
  return null;
}

/** Title + robots meta follow the route (libs/page-meta). */
function PageMeta() {
  const { pathname } = useLocation();
  useEffect(() => applyPageMeta(pathname), [pathname]);
  return null;
}

/** `<iframe>` map: no login, no app chrome — just the published style. */
function renderEmbed(basename) {
  createRoot(document.querySelector("#app")).render(
    <BrowserRouter basename={basename}>
      <PageMeta />
      <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/embed/:ref" element={<EmbedPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
  document.querySelector(".loading").style.display = "none";
}

/** How long the first render waits for the Keycloak SSO check. Usually
 * ~200 ms; past this the page renders as a guest (crawlers, slow Keycloak)
 * and re-renders signed in when the check comes back. */
const AUTH_WAIT_MS = 3000;

/** After sign-in: accounts, then consumers (they need the active account). */
function loadSession() {
  // Fire-and-forget — Save button will work once these resolve; we don't
  // want to block first paint.
  loadAccounts()
    .then(() => loadConsumers())
    .then(() => {
      const user = getUser();
      const consumer = getActiveConsumer();
      if (user && consumer) {
        toast.success(
          i18n.t("Đã đăng nhập với {{consumer}}", { consumer: consumer.name || consumer.id })
        );
      }
    });
}

async function bootstrap() {
  const embedBase = import.meta.env.BASE_URL.replace(/\/$/, "");
  if (window.location.pathname.startsWith(`${embedBase}/embed/`)) {
    renderEmbed(embedBase);
    return;
  }
  const root = createRoot(document.querySelector("#app"));
  // Vite serves the app under `import.meta.env.BASE_URL` (e.g. `/map-editor/`),
  // so router basename matches.
  const basename = import.meta.env.BASE_URL.replace(/\/$/, "");
  // `session` remounts everything when sign-in lands after the first render —
  // pages read the auth state once, on mount.
  const render = (session) => root.render(
    <IconContext.Provider value={{ className: "react-icons" }} key={session}>
      <BrowserRouter basename={basename}>
        <PageMeta />
        <Suspense fallback={<PageLoading />}>
          <Routes>
            {/* Root and /manage both land on the style library — root is the
                clean URL we share, /manage stays as a stable deep-link. */}
            <Route path="/" element={<ManagePage />} />
            <Route path="/manage" element={<ManagePage />} />
            <Route path="/create" element={<App />} />
            <Route path="/detail/:id" element={<DetailPage />} />
            <Route path="/compare" element={<ComparePage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <ToastViewport />
      <LoadingOverlay />
    </IconContext.Provider>
  );

  // Keycloak SSO check before rendering, so pages see a stable auth state
  // from the first render — but never wait on it for long.
  if (isAuthConfigured()) {
    const init = initAuth().catch((e) => {
      console.warn("Auth init failed, continuing as guest", e);
      return false;
    });
    const first = await Promise.race([init, new Promise((r) => setTimeout(() => r("timeout"), AUTH_WAIT_MS))]);
    if (first === "timeout") {
      init.then((authed) => {
        if (!authed) return;
        loadSession();
        render("signed-in");
      });
    } else if (first) {
      loadSession();
    }
  }

  render("first");

  // Hide the loader.
  document.querySelector(".loading").style.display = "none";
}

bootstrap();
